/**
 * Cross-account cleanup for signup / onboarding client state.
 *
 * The signup flow persists onboarding artefacts in the browser so the multi-MFE
 * hop (authn -> commerce -> Stripe -> authn) and the Stripe success/cancel
 * return pages can pick the flow back up:
 *   - localStorage `centrix_onboarding_token` — the in-flight onboarding token,
 *     written before redirecting to Stripe and read back (as a fallback) by the
 *     subscription success/cancel pages.
 *   - sessionStorage `centrix_signup_in_flight` / `centrix_email_verified:*` —
 *     signup security flags guarding the OTP and Sector Selection screens.
 *
 * None of these are tied to an authenticated session, so without an explicit
 * purge they survive a logout and a subsequent login by a *different* user on
 * the same browser. That leaks User A's pending checkout (the "Payment
 * cancelled" warning + "Try again" flow) into User B's session. Clearing this
 * state on every auth transition (login success and logout) guarantees a clean
 * boundary between accounts.
 */

const ONBOARDING_TOKEN_KEY = 'centrix_onboarding_token';
const SIGNUP_IN_FLIGHT_KEY = 'centrix_signup_in_flight';
const EMAIL_VERIFIED_PREFIX = 'centrix_email_verified:';

function safeStorage(get: () => Storage | undefined): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return get();
  } catch {
    // Storage can throw in private-browsing / sandboxed contexts.
    return undefined;
  }
}

function clearPrefixedKeys(storage: Storage, prefix: string): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => storage.removeItem(key));
}

/**
 * Removes every signup / onboarding artefact from browser storage. Safe to call
 * when storage is unavailable (no-ops). Call on login success and on logout so
 * that one user's in-flight onboarding never bleeds into another's session.
 */
export function clearOnboardingClientState(): void {
  const local = safeStorage(() => window.localStorage);
  if (local) {
    try {
      local.removeItem(ONBOARDING_TOKEN_KEY);
    } catch {
      // ignore
    }
  }

  const session = safeStorage(() => window.sessionStorage);
  if (session) {
    try {
      session.removeItem(SIGNUP_IN_FLIGHT_KEY);
      clearPrefixedKeys(session, EMAIL_VERIFIED_PREFIX);
    } catch {
      // ignore
    }
  }
}
